import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useState } from "react";

import { useVatCodeSaveMutation } from "src/app/features/VatCode/services/server/VatCodeApi";
import type { VatCodeEntity } from "src/app/features/VatCode/types/VatCodeEntity";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const VatCodeCreateForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("vat-code");

    const [formData, setFormData] = useState<VatCodeEntity>({} as VatCodeEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.VAT_CODES}`);
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useVatCodeSaveMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formSaveSuccess", { id: result.data.id, resource: "VatCode" }),
                    `${BASE_PATH}/${ResourceName.VAT_CODES}/${FORM_ACTION.EDIT}/${result.data.id}`,
                );
            }
        },
        [saveFormData, formData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<VatCodeEntity>
                    name="VatCodeCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
